import { FC, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Spacer } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import { useUpdateCreditCardStatusMutation } from '../../../hooks/useUpdateCreditCardStatusMutation';
import SidebarFooter from '../components/SidebarFooter';
import locales from './locales';
import {
  LocationState,
  OperationIdToStatusMapping,
  OperationIdToStatusMappingValue,
  UpdateStatusRouteParams,
} from './types';

interface UpdateStatusConfirmProps {
  creditCard: CreditCard;
}

const OPERATION_ID_STATUS_MAPPING: OperationIdToStatusMapping = {
  'lock-confirm': {
    cta: locales.lockCta,
    isDanger: true,
    status: 'LOCKED',
    subtitle: locales.lockSubtitle,
    successMessage: locales.lockSuccess,
    title: locales.lockTitle,
  },
  'lost-confirm': {
    cta: locales.permanentLockCta,
    isDanger: true,
    status: 'LOST',
    subtitle: locales.permanentLockSubtitle,
    successMessage: locales.permanentLockSuccess,
    title: locales.permanentLockTitle,
  },
  'stolen-confirm': {
    cta: locales.permanentLockCta,
    isDanger: true,
    status: 'STOLEN',
    subtitle: locales.permanentLockSubtitle,
    successMessage: locales.permanentLockSuccess,
    title: locales.permanentLockTitle,
  },
  'unlock-confirm': {
    cta: locales.unlockCta,
    isDanger: false,
    status: 'ACTIVE',
    subtitle: locales.unlockSubtitle,
    successMessage: locales.unlockSuccess,
    title: locales.unlockTitle,
  },
};

const UpdateStatusConfirm: FC<UpdateStatusConfirmProps> = ({ creditCard }) => {
  const { showToast } = useToast();
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const { operationId } = useParams<UpdateStatusRouteParams>();
  const previousOperationId = location.state?.previousOperationId || '';

  const { creditCardId } = creditCard;
  const { cta, isDanger, status, subtitle, successMessage, title } =
    OPERATION_ID_STATUS_MAPPING[
      operationId as string
    ] as OperationIdToStatusMappingValue;

  const [updateCreditCardStatus, { loading }] =
    useUpdateCreditCardStatusMutation();

  const onConfirm = useCallback(async () => {
    try {
      await updateCreditCardStatus({
        variables: {
          input: { creditCardId, status },
        },
      });
      showToast({ message: successMessage, type: 'success' });
      history.push(`/cards/${creditCardId}`);
    } catch (err) {
      showToast({ message: locales.errorMessage, type: 'error' });
    }
  }, [
    creditCardId,
    history,
    showToast,
    status,
    successMessage,
    updateCreditCardStatus,
  ]);

  return (
    <>
      <NavigationHeader
        backTo={`/cards/${creditCardId}/${previousOperationId}`}
      />

      <Heading marginBottom="space-16">{title}</Heading>
      <Text>{subtitle}</Text>

      <Spacer />

      <SidebarFooter>
        <Button
          isLoading={loading}
          onClick={onConfirm}
          variant={!isDanger ? 'primary' : 'danger'}
          width="full"
        >
          {cta}
        </Button>

        <Button
          as={Link}
          to={`/cards/${creditCardId}`}
          variant="inline-secondary"
        >
          {locales.cancel}
        </Button>
      </SidebarFooter>
    </>
  );
};

export default UpdateStatusConfirm;
